import { z } from 'zod';
/**
 * Asesores (advisors) y comisiones de adquisición.
 *
 * Las comisiones se almacenan y transmiten como FRACCIÓN (0.05 = 5%),
 * con un tope de negocio (hard cap) de 0.30 (30%).
 */
/**
 * Tipo de acuerdo con el asesor:
 *  - COMISION_PROYECTO: comisión sobre el ingreso de cada proyecto (entra en el CAP).
 *  - EQUITY: compensación en participaciones; no genera gasto por factura
 *    (la contabilización del equity queda fuera del flujo de comisiones).
 */
export const AdvisorAgreementTypeSchema = z.enum(['COMISION_PROYECTO', 'EQUITY']);
export const AdvisorDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    /** Comisión por defecto como fracción (0.05 = 5%). */
    defaultCommissionPct: z.number().nullable(),
    /** Organización a través de la que factura el asesor (si aplica). */
    organization: z.string().nullable(),
    /** Cuenta de cobro (IBAN) para automatizar remesas. */
    payoutIban: z.string().nullable(),
    agreementType: AdvisorAgreementTypeSchema,
    notes: z.string().nullable(),
    active: z.boolean(),
});
/**
 * Lo que ve de un asesor quien NO es ADMIN (los selectores de la UI): sin IBAN ni notas. ☠️ El IBAN es el dato bancario de
 * un tercero y las notas son internas (pactos, condiciones): solo el ADMIN que gestiona a los asesores los necesita.
 */
export const AdvisorPublicDtoSchema = AdvisorDtoSchema.omit({ payoutIban: true, notes: true });
export const CreateAdvisorSchema = z.object({
    name: z.string().min(1),
    email: z.string().email().nullable().optional(),
    /** Comisión por defecto como fracción (0.05 = 5%). Tope de negocio: 0.30 (30%). */
    defaultCommissionPct: z.number().min(0).max(0.3).nullable().optional(),
    organization: z.string().max(300).nullable().optional(),
    /** IBAN sin validación de checksum; se normaliza quitando espacios y a mayúsculas. */
    payoutIban: z
        .string()
        .max(50)
        .nullable()
        .optional()
        .transform((v) => (v ? v.replace(/\s+/g, '').toUpperCase() : v)),
    agreementType: AdvisorAgreementTypeSchema.default('COMISION_PROYECTO'),
    notes: z.string().nullable().optional(),
    active: z.boolean().default(true),
});
export const UpdateAdvisorSchema = CreateAdvisorSchema.partial();
// ---------------------------------------------------------------------------
// Detalle y liquidación por asesor (Ajustes → Asesores → Detalle)
// ---------------------------------------------------------------------------
/**
 * Sobre qué se calcula la comisión de una línea:
 *  - FACTURADO: base imponible facturada en el año (comisión de proyecto; las mismas facturas que cuenta la rentabilidad).
 *  - VALOR_TRATO: valor del trato ganado en el año (equity).
 */
export const AdvisorBasisSchema = z.enum(['FACTURADO', 'VALOR_TRATO']);
/** Una oportunidad en el desglose de un asesor. Importes en céntimos. */
export const AdvisorSummaryLineSchema = z.object({
    opportunityId: z.string(),
    code: z.string(),
    title: z.string(),
    clientName: z.string(),
    stage: z.string(),
    /** Fracción (0.08 = 8 %). null = la oportunidad no tiene comisión asignada. */
    commissionPct: z.number().nullable(),
    basis: AdvisorBasisSchema,
    /** Valor del trato (facturable + no facturable, o el estimado). */
    dealValueCents: z.number().int(),
    /** La base de la comisión: lo facturado en el año o el valor del trato, según `basis`. */
    baseCents: z.number().int(),
    commissionCents: z.number().int(),
});
export const AdvisorSettlementDtoSchema = z.object({
    id: z.string(),
    advisorId: z.string(),
    year: z.number().int(),
    seq: z.number().int(),
    amountCents: z.number().int(),
    accruedCents: z.number().int(),
    previousSettledCents: z.number().int(),
    baseCents: z.number().int(),
    lines: z.array(AdvisorSummaryLineSchema),
    notes: z.string().nullable(),
    settledAt: z.string().datetime(),
    settledByName: z.string().nullable(),
    sentAt: z.string().datetime().nullable(),
    sentTo: z.string().nullable(),
    voidedAt: z.string().datetime().nullable(),
    voidedByName: z.string().nullable(),
});
export const AdvisorSummarySchema = z.object({
    advisor: AdvisorDtoSchema,
    year: z.number().int(),
    /** Años con actividad (o el actual), para el selector. */
    years: z.array(z.number().int()),
    basis: AdvisorBasisSchema,
    lines: z.array(AdvisorSummaryLineSchema),
    totals: z.object({
        /** Valor de los tratos ganados en el año con este asesor. */
        soldCents: z.number().int(),
        baseCents: z.number().int(),
        /** Comisión devengada en el año. */
        accruedCents: z.number().int(),
        /** Lo ya liquidado (sin contar las liquidaciones anuladas). */
        settledCents: z.number().int(),
        /** Devengado − liquidado. Negativo si después de liquidar se anuló o rectificó una factura. */
        pendingCents: z.number().int(),
    }),
    /** Tratos de este asesor aún en curso (ni ganados ni perdidos). */
    pipeline: z.object({ count: z.number().int(), valueCents: z.number().int() }),
    settlements: z.array(AdvisorSettlementDtoSchema),
});
export const AdvisorYearQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export const CreateAdvisorSettlementSchema = z.object({
    year: z.number().int().min(2000).max(2100),
    notes: z.string().max(1000).nullable().optional(),
    /** El pendiente que quien liquida está viendo (céntimos): si el servidor calcula otro, responde 409 y no liquida. */
    expectedPendingCents: z.number().int().optional(),
});
export const AdvisorReportQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    /** Sin él, el informe es el del año hasta hoy (provisional); con él, el documento de esa liquidación. */
    settlementId: z.string().optional(),
});
export const SendAdvisorReportSchema = z.object({
    year: z.number().int().min(2000).max(2100),
    settlementId: z.string().optional(),
    /** Texto libre que se añade al cuerpo del correo. */
    message: z.string().max(2000).optional(),
});
//# sourceMappingURL=advisors.js.map